import { Badge, HStack, VStack } from "@chakra-ui/react";
import { DistributionTiersBreakdown } from "features/distributions/DistributionTiersBreakdown";
import { useExternalIDsRetrieveSuspense } from "gen/endpoints/external-id/external-id";
import { useHypotheticalWaterfallDetailsBreakdownRetrieveSuspense } from "gen/endpoints/financial-reporting/financial-reporting";
import { useInvestorEntityRetrieveSuspense } from "gen/endpoints/investor-entity/investor-entity";
import { WaterfallTierTypeEnum } from "gen/model/waterfallTierTypeEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ClickableElementGenericLinkInfo } from "shared/components/clickable/ClickableElementLinkInfo";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { DataTableCellLink } from "shared/components/table/data/DataTableCellLink";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import { RenderCellValueFn } from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const HypotheticalWaterfallInvestorBreakdown: React.FC = () => {
  const {
    fundFamilyId,
    hypotheticalWaterfallId,
    endOfPeriodReportId,
    fundEntityId,
    investorEntityId,
  } = useRequiredStringParams();
  const { t } = useTranslation(["common", "financialReporting", "waterfall"]);

  const { data: investorEntity } =
    useInvestorEntityRetrieveSuspense(investorEntityId);

  const { data: hypotheticalInvestorData } =
    useHypotheticalWaterfallDetailsBreakdownRetrieveSuspense(
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
      {
        fund_id: fundEntityId,
        investor_entity_id: investorEntityId,
      },
    );

  const renderCellValue = useCallback<RenderCellValueFn>(
    (props, fieldDisabled, params) => {
      const baseRoute = rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .financialReporting({});
      const renderedValue = defaultDataTableRenderCellValue(
        props,
        fieldDisabled,
        params,
      );
      const { cell } = props;
      if (
        cell.metadata?.type == "waterfall_tier" &&
        cell.metadata.tier_type == WaterfallTierTypeEnum.preferred_return &&
        cell.metadata.waterfall_tier_mapping_id
      ) {
        const linkInfo: ClickableElementGenericLinkInfo = {
          type: "generic",
          variant: "tableCellAction",
          to: baseRoute
            .hypotheticalWaterfall({
              endOfPeriodReportId,
              hypotheticalWaterfallId,
            })
            .investorPrefBreakdown({
              fundEntityId,
              waterfallTierMappingId: cell.metadata.waterfall_tier_mapping_id,
              investorEntityId,
            }).$,
        };
        return (
          <>
            {renderedValue}
            <DataTableCellLink
              tableContext={props.tableContext}
              columnContext={props.columnContext}
              row={props.row}
              linkInfo={linkInfo}
              onButtonClick={undefined}
            >
              {t("common:viewDetails")}
            </DataTableCellLink>
          </>
        );
      }

      return renderedValue;
    },
    [
      fundFamilyId,
      fundEntityId,
      hypotheticalWaterfallId,
      endOfPeriodReportId,
      investorEntityId,
      t,
    ],
  );

  const { data: externalIdList } =
    useExternalIDsRetrieveSuspense(investorEntityId);

  return (
    <PageContent>
      <PageHeader
        title={t("waterfall:review.investorLevel.title")}
        badge={
          <HStack spacing="8px">
            <Badge>{investorEntity.legal_name}</Badge>
            {externalIdList.map((externalID) => (
              <Badge key={externalID.id}>{externalID.name}</Badge>
            ))}
          </HStack>
        }
        backButton={{
          title: t("waterfall:review.title"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .financialReporting({})
            .hypotheticalWaterfall({
              hypotheticalWaterfallId,
              endOfPeriodReportId,
            })
            .review({})
            .waterfallDetails({}).$,
        }}
      />
      <VStack alignItems="stretch">
        <TableBox>
          <TableBoxHeader>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable
              data={hypotheticalInvestorData.waterfall_results_table}
              renderCellValue={renderCellValue}
            />
          </TableBoxBody>
        </TableBox>
        <DistributionTiersBreakdown data={hypotheticalInvestorData} />
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>{t("common:carry")}</TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={hypotheticalInvestorData.carry_breakdown_table!} />
          </TableBoxBody>
        </TableBox>
      </VStack>
    </PageContent>
  );
};
